import { toast } from '../../../utils/toast';
import { apiPost, apiGet, handleApiError, showErrorToast } from '../../../services/apiClient';
import { OfferStatus, PotentialOffer, TruckDetails } from '../../../types';
import useConfirm from '../../../hooks/ui/useConfirm';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Negotiation, NegotiationStartRequest } from '../../../models/negotiation';
import { resolveNegotiationForOffer, resolveNegotiationIdForOffer } from '../../../utils/negotiationOfferUtils';
import { NegotiationParams } from '../../../components/dialogs';
import { triggerNegotiationAlertsRefresh } from '../../../context/negotiationAlertsContext';

export const useNegotiationHandlers = () => {

  const [negotiation, setNegotiation] = useState<Negotiation| null>(null);

  const { t } = useTranslation();
  const {
    ConfirmModal,
    setConfirmIsOpen,
    confirm,
    setConfirmTitle,
    setConfirmDescription,
    setConfirmText
  } = useConfirm();


  const handleFetchNegotiation = async (vehicleId: string, id: string): Promise<Negotiation> => {
    const response = await apiGet(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${vehicleId}/negotiation/${id}/info`);
    const data = await response.json();
    setNegotiation(data.result);
    return data.result;
  };

  const handleStartNegotiation = async (vehicleId: string, offerId: number, formData: NegotiationParams): Promise<{
    negotiationId: string;
    messageId: string;
    messagesHistory: string[];
  }> => {

    const requestData: NegotiationStartRequest = {
      estimated_price: formData.estimatedPrice,
      fair_price: formData.fairPrice,
      minimum_acceptance: formData.minAccept,
      target_price: formData.targetPrice,
      bad_situation: formData.badSituation,
      auto_respond: formData.autoRespond,
      user_own_name: formData.userOwnName,
      vehicle_auto_nego_session_id: formData.vehicle_auto_nego_session_id,
      min_accept: formData.minAccept,
      good_situation: formData.goodSituation,
      local_nego: formData.localNego ? formData.localNego : false,
      freight_exchange_format: formData.freightExchangeFormat ? formData.freightExchangeFormat : false,
      auto_send_initial: formData.autoSendInitial ? formData.autoSendInitial : false,
      ltl_applied: true,
      use_messenger: formData.useMessenger === true,
    };

    const response = await apiPost<NegotiationStartRequest>(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${vehicleId}/offer/${offerId}/negotiate`, requestData);
    const data = await response.json();
    return data?.result;
  };

  // Core function to accept negotiation offer.
  const acceptNegotiationAPI = async (vehicle: TruckDetails | null, offer: PotentialOffer, back_confirm?: boolean, send_email?: boolean, email_response?: string, include_plates?: boolean) => {
    if (!vehicle) return;

    try {
      const vehicleResponse = await apiGet(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${vehicle.id}`);

      const vehicleData = await vehicleResponse.json();
      const negotiation = resolveNegotiationForOffer(offer, vehicleData.result.negotiations);
      const negotiationId = resolveNegotiationIdForOffer(offer, negotiation);
      const truckInfo = vehicleData.result;
      let autoRespond = negotiation?.autoRespond;

      // Fetch autoRespond from VehicleAutoNegoSession if available
      if (negotiation?.vehicle_auto_nego_session_id) {
        try {
          const negoSessionResponse = await apiGet(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${vehicle.id}/auto_nego_session/${negotiation.vehicle_auto_nego_session_id}`);
          const negoSessionData = await negoSessionResponse.json();
          autoRespond = negoSessionData.result?.autoRespond || false;
        } catch (err) {
          showErrorToast(err, t('Failed to fetch auto negotiation session details'));
        }
      }

      if (!negotiationId) {
        showErrorToast(undefined, t('Negotiation not found'));
        return;
      }

      await apiPost(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${vehicle.id}/negotiation/${negotiationId}/accept`, { send_email: autoRespond || send_email, back_confirm: back_confirm, ...(email_response ? { email_response } : {}), ...(include_plates ? { include_plates } : {}) });
      triggerNegotiationAlertsRefresh();

      const truckPlates = truckInfo.truckPlate || truckInfo.plate || 'N/A';
      const trailerPlates = truckInfo.trailerPlate || 'N/A';
      const platesText = trailerPlates !== 'N/A' ? `${truckPlates} / ${trailerPlates}` : truckPlates;

      toast.success(offer.status === OfferStatus.NEGOTIATION_ACCEPTED ? `Offer for truck ${platesText} validated! Email sent to client.` : `Order confirmed. Booking created for truck ${platesText}.`);

    } catch (err: any) {
      const errorKey = err?.errorData?.error?.additionalInfo || err?.errorData?.error?.errorKey;
      if (errorKey === 'client.negotiation.pendingAiMessage') {
        toast.error('Cannot order confirm while there are unsent messages. Please send or discard them first.');
      } else {
        handleApiError(err, t('Failed to confirm order'));
      }
    } finally {
      setConfirmIsOpen(false);
    }
  };

  /**
   * Accept negotiation offer with confirmation dialog.
   * Sends AI message but does NOT create booking.
   * Used in: PotentialMatchesTable action buttons
   */
  const handleAcceptNegotiation = async (vehicle: TruckDetails | null, offer: PotentialOffer, email_response?: string, include_plates?: boolean) => {
    if (!vehicle) return;

    setConfirmTitle(t('Accept Negotiation'));
    setConfirmDescription(t('Are you sure you want to accept this negotiation?'));
    setConfirmText(t('Accept'));

    const confirmed = await confirm();
    if (!confirmed) return;

    return acceptNegotiationAPI(vehicle, offer, false, true, email_response, include_plates); // back_confirm = false → will not create booking & send_email = true → will send pending AI message
  };

  /**
   * Order confirm negotiation with confirmation dialog.
   * Creates booking but does NOT send AI message.
   * Used in: PotentialMatchesTable and NegotiationForm
   */
  const handleOrderConfirmNegotiation = async (vehicle: TruckDetails | null, offer: PotentialOffer) => {
    if (!vehicle) return;

    setConfirmTitle(t('Order Confirm Negotiation'));
    setConfirmDescription(t('Are you sure you want to order confirm this negotiation?'));
    setConfirmText(t('Order Confirm'));

    const confirmed = await confirm();
    if (!confirmed) return;

    return acceptNegotiationAPI(vehicle, offer, true, false); // back_confirm = true → will create booking
  };

  /**
   * Abort negotiation with confirmation dialog.
   * Used in: PotentialMatchesTable and NegotiationForm
   */
  const handleAbortNegotiation = async (vehicle: TruckDetails | null, offer: PotentialOffer) => {
    if (!vehicle) return;

    setConfirmTitle(t('Abort Negotiation'));
    setConfirmDescription(t('Are you sure you want to abort this negotiation?'));
    setConfirmText(t('Abort'));

    const confirmed = await confirm();
    if (!confirmed) return;

    try {
      let negotiation = resolveNegotiationForOffer(offer, undefined);
      let negotiationId = resolveNegotiationIdForOffer(offer, negotiation);

      if (negotiationId === undefined) {
        const vehicleResponse = await apiGet(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${vehicle.id}`);
        const vehicleData = await vehicleResponse.json();
        negotiation = resolveNegotiationForOffer(offer, vehicleData.result.negotiations);
        negotiationId = resolveNegotiationIdForOffer(offer, negotiation);
      }

      if (!negotiationId) {
        showErrorToast(undefined, t('Negotiation not found'));
        return;
      }
      const localNego = !!localStorage.getItem('localNego');
      await apiPost(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${vehicle.id}/negotiation/${negotiationId}/cancel`, { send_email: localNego });
      toast.success(t('Negotiation cancelled successfully'));
      triggerNegotiationAlertsRefresh();


    } catch (err: any) {
      handleApiError(err, t('Failed to cancel negotiation'));
    } finally {
      setConfirmIsOpen(false);
    }
  };

  return {
    handleStartNegotiation,
    handleAcceptNegotiation, // Accept with confirmation (sends AI message, no booking)
    handleOrderConfirmNegotiation, // Order confirm with confirmation (creates booking)
    handleAbortNegotiation, // Abort with confirmation
    handleFetchNegotiation,
    acceptNegotiationCore: acceptNegotiationAPI, // Direct core function for custom flows (NegotiationForm)
    setNegotiation,
    negotiation,
    ConfirmNegotiationModal: ConfirmModal
  };
};
